import { create } from 'zustand';

interface HistoryState {
  history: string[];
  historyIndex: number;
  maxHistory: number;
  isRestoring: boolean;

  // Actions
  saveSnapshot: (state: string) => void;
  undo: () => string | null;
  redo: () => string | null;
  clear: () => void;
  setRestoring: (isRestoring: boolean) => void;

  // Computed
  canUndo: () => boolean;
  canRedo: () => boolean;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  history: [],
  historyIndex: -1,
  maxHistory: 50,
  isRestoring: false,

  saveSnapshot: (state: string) => {
    const { history, historyIndex, maxHistory, isRestoring } = get();

    // Don't save while restoring a state
    if (isRestoring) return;

    // Remove any future history if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);

    // Add new state
    newHistory.push(state);

    // Trim if exceeding max
    if (newHistory.length > maxHistory) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  undo: () => {
    const { history, historyIndex } = get();

    if (historyIndex <= 0) return null;

    const newIndex = historyIndex - 1;
    set({ historyIndex: newIndex });

    return history[newIndex] ?? null;
  },

  redo: () => {
    const { history, historyIndex } = get();

    if (historyIndex >= history.length - 1) return null;

    const newIndex = historyIndex + 1;
    set({ historyIndex: newIndex });

    return history[newIndex] ?? null;
  },

  clear: () => {
    set({
      history: [],
      historyIndex: -1,
    });
  },

  setRestoring: (isRestoring: boolean) => {
    set({ isRestoring });
  },

  canUndo: () => {
    const { historyIndex } = get();
    return historyIndex > 0;
  },

  canRedo: () => {
    const { history, historyIndex } = get();
    return historyIndex < history.length - 1;
  },
}));

// Selectors
export const selectHistoryState = (state: HistoryState) => ({
  canUndo: state.canUndo(),
  canRedo: state.canRedo(),
  historyLength: state.history.length,
  currentIndex: state.historyIndex,
});
